import React from 'react';
import { styled } from '@linaria/react';

import { range, normalize, clamp } from '@/utils';
import useMousePositionWithinElement from '@/hooks/use-mouse-position-within-element';

interface Props {
  hue: number;
  saturation: number;
  lightness: number;
  thickness?: number;
  fadeDelay: number;
  onChange: (params: { hue: number }) => void;
}

const HueBar = ({
  hue,
  saturation,
  lightness,
  thickness = 24,
  fadeDelay,
  onChange,
}: Props) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [isMouseDown, setIsMouseDown] = React.useState(false);

  const [mousePosition, boundingBox] = useMousePositionWithinElement(
    wrapperRef,
    { buffer: 250 }
  );

  React.useEffect(() => {
    if (
      !isMouseDown ||
      typeof mousePosition.x !== 'number' ||
      !boundingBox
    ) {
      return;
    }

    const percentageThrough =
      (mousePosition.x / boundingBox.width) * 100;

    const hue = Math.round(
      clamp(normalize(percentageThrough, 0, 100, 0, 360), 0, 360)
    );

    onChange({ hue });
  }, [isMouseDown, mousePosition, boundingBox, onChange]);

  React.useEffect(() => {
    function handleMouseUp() {
      setIsMouseDown(false);
    }

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isMouseDown]);

  function handleKeyDown(ev: React.KeyboardEvent) {
    switch (ev.key) {
      case 'ArrowLeft': {
        ev.preventDefault();

        onChange({ hue: (hue + 360 - 5) % 360 });

        return;
      }
      case 'ArrowRight': {
        ev.preventDefault();

        onChange({ hue: (hue + 5) % 360 });

        return;
      }
    }
  }

  const indicatorPosition =
    typeof hue === 'number' && normalize(hue, 0, 360, 0, 100);

  const backgroundGradientStops = generateHueGradient({
    saturation,
    lightness,
  });

  return (
    <Wrapper
      ref={wrapperRef}
      style={{
        '--thickness': thickness + 'px',
        '--gradient-stops': backgroundGradientStops,
        '--fade-delay': fadeDelay + 'ms',
      }}
      onMouseDown={(ev) => {
        // Prevent text selection
        ev.preventDefault();
        setIsMouseDown(true);
      }}
      onTouchStart={(ev) => {
        // Prevent text selection
        ev.preventDefault();
        setIsMouseDown(true);
      }}
    >
      {typeof hue === 'number' && (
        <Indicator
          style={{
            '--percentage': indicatorPosition + '%',
            '--indicator-background': `hsl(${hue}deg ${
              saturation * 100
            }% ${lightness * 100}%)`,
          }}
          onKeyDown={handleKeyDown}
        />
      )}
    </Wrapper>
  );
};

function generateHueGradient({
  saturation,
  lightness,
}: {
  saturation: number;
  lightness: number;
}) {
  const STEPS = 360;

  return range(STEPS)
    .map((i) => {
      const degree = normalize(i, 0, STEPS, 0, 360);
      return `hsl(${degree}deg, ${saturation * 100}%, ${
        lightness * 100
      }%)`;
    })
    .join(', ');
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: var(--thickness);
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(90deg, var(--gradient-stops));
  border-radius: 4px;
  cursor: pointer;
  border: var(--white-border-thickness) solid white;
  touch-action: none;
  box-shadow: var(--shadow-elevation-medium);
  animation: fadeIn 250ms both;
  animation-delay: var(--fade-delay);
`;

const Indicator = styled.button`
  position: absolute;
  top: 0;
  left: var(--percentage);
  width: 16px;
  height: calc(var(--thickness) + var(--white-border-thickness) * 2);
  border: 2px solid black;
  transform: translate(
    -50%,
    calc(var(--white-border-thickness) * -2)
  );
  transform-origin: center center;
  box-shadow: inset 0px 0px 0px 2px white;
  background: var(--indicator-background);
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  &:focus {
    outline-offset: 4px;
  }
`;

export default HueBar;
